import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode } from '@craftjs/core';

import SelectColor from '@resolvers/tools/SelectColor/SelectColor';
import { BORDER_OPTIONS, EDITOR_RESOLVERS } from '@common/constants';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';
import BorderStyle from '@resolvers/tools/BorderStyle/BorderStyle';
import { getFlex } from '@common/helpers/domManipulationHelpers';
import FlexAlignment from '@resolvers/tools/FlexAlignment/FlexAlignment';

const PdfContainer = ({
  background,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  borderColor,
  borderLeft,
  borderBottom,
  borderTop,
  borderRight,
  width,
  height,
  children,
  vAlign,
  align,
  flexDirection,
  flexOrder,
  justifyContent,
  alignItems,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const { t } = useTranslation();
  const ref = useRef(null);

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    setCustom((props) => {
      props.displayName = t('container');
    }, 500);
  }, [setCustom, t]);

  const flexValues = getFlex({ flexOrder, flexDirection });

  return (
    <div
      ref={ref}
      className='pdf-container'
      style={{
        backgroundColor: background,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
        borderLeft: `${borderLeft ? `1px solid ${borderColor}` : ''}`,
        borderBottom: `${borderBottom ? `1px solid ${borderColor}` : ''}`,
        borderTop: `${borderTop ? `1px solid ${borderColor}` : ''}`,
        borderRight: `${borderRight ? `1px solid ${borderColor}` : ''}`,
        minHeight: `${height}px`,
        width: `${width}%`,
        alignItems: vAlign,
        justifyContent: align,
        boxSizing: 'border-box',
        rowGap: 0,
        columnGap: 0,
        gap: 0,
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        display: 'flex',
        flexWrap: 'wrap',
        hOverflow: 'hidden',
        alignItems,
        justifyContent,
        ...flexValues,
      }}
    >
      {children}
    </div>
  );
};

const ContainerConfigPanel = () => {
  return (
    <SettingsWrapper>
      <SettingsRow sx={{ mb: 2 }}>
        <SizeStyle propKey='width' label='width' blockOutboundValues={true} />
      </SettingsRow>
      <PaddingStyle />
      <FlexAlignment />
      <SettingsRow sx={{ mb: 3 }}>
        <BorderStyle options={BORDER_OPTIONS} />
      </SettingsRow>
      <SettingsRow>
        <SelectColor />
      </SettingsRow>
    </SettingsWrapper>
  );
};

export const ContainerDefaultProps = {
  background: '#ffffff',
  borderColor: '#F6EDED',
  borderAllShow: true,
  borderLeft: false,
  borderBottom: false,
  borderTop: false,
  borderRight: false,
  borderAllSize: '1',
  borderAllType: 'solid',
  paddingLeft: '10',
  paddingRight: '10',
  paddingTop: '10',
  paddingBottom: '10',
  height: '100',
  vAlign: 'flex-start',
  align: 'flex-start',
  children: null,
  width: 100,
  flexDirection: 'column',
  flexOrder: 'normal',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  isDeletable: true,
};

PdfContainer.defaultProps = ContainerDefaultProps;

PdfContainer.propTypes = {
  background: PropTypes.string,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  borderLeft: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderTop: PropTypes.bool,
  borderRight: PropTypes.bool,
  vAlign: PropTypes.string,
  align: PropTypes.string,
  height: PropTypes.string,
  children: PropTypes.node,
  flexDirection: PropTypes.string,
  flexOrder: PropTypes.string,
  justifyContent: PropTypes.string,
  alignItems: PropTypes.string,
};

PdfContainer.craft = {
  displayName: EDITOR_RESOLVERS.PDF.CONTAINER,
  name: EDITOR_RESOLVERS.PDF.CONTAINER,
  props: ContainerDefaultProps,
  related: {
    settings: ContainerConfigPanel,
  },
};

export default PdfContainer;
